import React, { ReactElement, useState } from "react";
import { ThemeProvider, Button, Grid } from "@material-ui/core";
import theme from "../../../themes";
import { useFormik } from "formik";
import * as yup from "yup";
import { makeStyles } from "@material-ui/core/styles";
import Logo from "../../../images/Gold_Logo.png";
import { Auth } from "aws-amplify";
import { RedirectUri, useEnumStrings } from "../../enumStrings";
import { commonCognitoPasswordRegex } from "../../common/utilities/commonPasswordRegex";
import PasswordField from "../../common/PasswordField";
import {
  UserType,
  useSetHasSignedUpForChamberMemberMutation,
} from "../../../graphql/types";
import {
  clearPendingNewPasswordChallenge,
  getPendingNewPasswordChallenge,
} from "./pendingNewPasswordChallenge";
import { getSingleCognitoUserTypeFromGroups } from "../../common/utilities/adminConsoleUserType";

interface NewPasswordInput {
  newPassword: string;
  confirmPassword: string;
}
const useStyles = makeStyles((theme) => ({
  root: {
    "& .MuiTextField-root": {
      margin: theme.spacing(1),
      width: "25ch",
      marginLeft: "auto",
      marginRight: "auto",
    },
  },
  loginForm: {
    height: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  img: {
    width: "250px",
    display: "block",
    margin: "auto",
    paddingTop: "9.2px",
  },
  heading: {},
  formFields: {
    display: "flex",
    flexDirection: "column",
    maxWidth: "500px",
    height: "500px",
    background: "white",
    padding: "29px",
    boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
    position: "absolute",
    left: "0",
    right: "0",
    top: "0",
    bottom: "0",
    margin: "auto",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "center",
    paddingTop: "22px",
    paddingBottom: "9px",
    flexDirection: "column",
    alignItems: "center",
  },
  textField: {
    backgroundColor: "white",
  },
  textFieldLabel: {
    fontSize: "18px",
  },
  formBody: {
    width: "90%",
    maxWidth: "585px",
  },
  invalidError: {
    textAlign: "center",
    color: "red",
  },
  icon: {
    margin: "auto",
  },
}));
const NewPassword = (): ReactElement => {
  const classes = useStyles();
  const enumStrings = useEnumStrings();
  const initialValues: NewPasswordInput = {
    newPassword: "",
    confirmPassword: "",
  };

  const pendingChallenge = getPendingNewPasswordChallenge();
  const [invalidError, setInvalidError] = useState(!pendingChallenge);
  const [customError, setCustomError] = useState(
    pendingChallenge
      ? ""
      : "Your temporary sign-in session has expired. Return to login and try again."
  );
  const [loader, setLoader] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [challengeCommitted, setChallengeCommitted] = useState(false);

  const [setHasSignedUpForChamberMember] =
    useSetHasSignedUpForChamberMemberMutation();

  const validationSchema = yup.object({
    newPassword: yup
      .string()
      .required(enumStrings.newPasswordValidation.newPassword)
      .matches(
        commonCognitoPasswordRegex,
        enumStrings.newPasswordValidation.newPasswordmatches
      ),
    confirmPassword: yup
      .string()
      .oneOf(
        [yup.ref("newPassword"), null],
        enumStrings.newPasswordValidation.confirmPasswordoneOf
      ),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (input: NewPasswordInput) => {
      if (!pendingChallenge) {
        setInvalidError(true);
        setCustomError(
          "Your temporary sign-in session has expired. Return to login and try again."
        );
        return;
      }

      setLoader(true);
      setInvalidError(false);
      setCustomError("");
      let passwordWasCommitted = false;
      try {
        const user = await Auth.completeNewPassword(
          pendingChallenge.user,
          input.newPassword.trim()
        );
        clearPendingNewPasswordChallenge();
        sessionStorage.removeItem(
          enumStrings.sessionStorageKey.loggedInWithTemporaryPassword
        );
        passwordWasCommitted = true;
        setChallengeCommitted(true);

        const userType = getSingleCognitoUserTypeFromGroups(
          user?.signInUserSession?.accessToken?.payload?.["cognito:groups"]
        );
        const customAccessTokenForSettingHasSignedUp =
          user?.signInUserSession?.accessToken?.jwtToken;

        if (!userType || !customAccessTokenForSettingHasSignedUp) {
          throw new Error("The completed sign-in session is invalid.");
        }

        if (userType === UserType.ChamberMemberAdmin) {
          await setHasSignedUpForChamberMember({
            context: {
              customAccessTokenForSettingHasSignedUp,
            },
          });
        }

        setButtonDisabled(true);
        window.location.href = RedirectUri.home;
      } catch (error) {
        setInvalidError(true);
        if (passwordWasCommitted) {
          try {
            await Auth.signOut();
          } catch (_signOutError) {
            // API authorization still evaluates the staff token. Keep the
            // recovery message reachable if local sign-out cleanup fails.
          }
          setCustomError(
            "Your password was set. Return to login with your new password."
          );
        } else if (error?.code === "NetworkError") {
          setCustomError("Please connect to the internet");
        } else {
          setCustomError(
            "Your password could not be set. Check the requirements and try again."
          );
        }
        setButtonDisabled(false);
      } finally {
        setLoader(false);
      }
    },
  });

  return (
    <ThemeProvider theme={theme}>
      <div>
        <form
          style={{
            height: "600px",
          }}
          onSubmit={formik.handleSubmit}
          className={classes.formFields}
        >
          <img
            src={Logo}
            width="180px"
            height="180px"
            className={classes.icon}
            alt="capIcon"
          />
          <br />
          <div style={{ textAlign: "center" }}>
            <h3>{enumStrings.newPasswordText.pleaseEnterNewPasswordText}</h3>
          </div>
          <div style={{ margin: "-10px" }} />
          <Grid item xs={12}>
            <PasswordField
              id="newPassword"
              fieldName="newPassword"
              label={enumStrings.newPasswordText.newPasswordLabel}
              value={formik.values.newPassword}
              onChange={formik.handleChange}
              error={formik.errors.newPassword}
              touched={formik.touched.newPassword}
              hideText
              showVisibilityEyeball
            />
            <PasswordField
              id="confirmPassword"
              fieldName="confirmPassword"
              label={enumStrings.newPasswordText.confirmPasswordLabel}
              value={formik.values.confirmPassword}
              onChange={formik.handleChange}
              error={formik.errors.confirmPassword}
              touched={formik.touched.confirmPassword}
              hideText
              showVisibilityEyeball
            />
          </Grid>
          <div className={classes.buttonContainer}>
            <br />
            <Button
              type="submit"
              size="large"
              color="primary"
              variant="contained"
              disabled={
                buttonDisabled ||
                challengeCommitted ||
                !pendingChallenge ||
                loader
              }
            >
              {enumStrings.newPasswordText.changePasswordText}
            </Button>
            <br />
            <span
              style={{
                textAlign: "center",
                color: "#E1A731",
                textDecoration: "underline",
                fontWeight: "bold",
                cursor: "pointer",
              }}
              onClick={() => {
                clearPendingNewPasswordChallenge();
                sessionStorage.removeItem(
                  enumStrings.sessionStorageKey.loggedInWithTemporaryPassword
                );
                void Auth.signOut();
                window.location.href = RedirectUri.login;
              }}
            >
              Back to login
            </span>
          </div>
          {invalidError ? (
            <div className={classes.invalidError}>{customError}</div>
          ) : null}{" "}
        </form>
      </div>
    </ThemeProvider>
  );
};

export default NewPassword;
